import Interact from 'interactjs'
import Vue from 'vue'
import Component from 'vue-class-component'
import { ColumnsData, isColumns } from '../../store/editor/components/state'
import * as EditorCoreHelpers from '../../store/editor/core/helpers'
import * as ComponentsHelpers from '../../store/editor/components/helpers'
import { Store } from 'vuex'
import RootState from '../../store/state'
import ColumnSizes from '../../misc/column-sizes'

@Component({
  props: {
    columnId: {
      type: String,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  }
})
export default class ColumnSeparatorVue extends Vue {
  readonly $store: Store<RootState>
  private readonly columnId: string
  private readonly index: number
  private interactable: Interact.Interactable

  get leftColumnId(): string {
    return this.getColumnItemId(this.index - 1)
  }

  get rightColumnId(): string {
    return this.getColumnItemId(this.index)
  }

  get leftColumn(): HTMLElement {
    return document.getElementById(this.leftColumnId)
  }

  get rightColumn(): HTMLElement {
    return document.getElementById(this.rightColumnId)
  }

  get columnSizes(): number[] {
    const data = this.getColumnsData()
    if (!!data.itemSizes && data.itemSizes.length === data.childIds.length) {
      // clone Array
      return data.itemSizes.concat()
    }
    return ColumnSizes.ofNaturalSizes(ColumnsData.MAX_COLUMN_SIZE, data.childIds.length).itemSizes
  }

  get leftColumnSize(): number {
    return this.columnSizes[this.index - 1]
  }

  get rightColumnSize(): number {
    return this.columnSizes[this.index]
  }

  get totalColumnSize(): number {
    return this.leftColumnSize + this.rightColumnSize
  }

  mounted() {
    this.interactable = Interact(this.$el)
    this.interactable.draggable({}).on('dragstart', (event: Interact.InteractEvent) => {
      this.$el.classList.add('separator--dragging')
      ComponentsHelpers.Actions.requestSaveState(this.$store)
      EditorCoreHelpers.Mutations.clearFocus(this.$store)
    }).on('dragmove', (event: Interact.InteractEvent) => {
      const columnMoveX = this.getColumnMoveX(event)
      const gridWidth = this.getGridWidth()
      this.$el.style.transform = `translate(${columnMoveX * gridWidth}px, 0)`
    }).on('dragend', (event: Interact.InteractEvent) => {
      this.$el.classList.remove('separator--dragging')
      this.$el.style.transform = ''
      const columnMoveX = this.getColumnMoveX(event)
      const newItemSizes = this.columnSizes
      newItemSizes[this.index - 1] = newItemSizes[this.index - 1] + columnMoveX
      newItemSizes[this.index] = newItemSizes[this.index] - columnMoveX
      this.$emit('resize', newItemSizes)
    })
  }

  destroyed() {
    this.interactable.unset()
  }

  getColumnsData(): ColumnsData {
    const data = ComponentsHelpers.Getters.componentFinder(this.$store)(this.columnId)
    if (isColumns(data)) {
      return data
    }
    throw new TypeError(`列コンポーネントのIDではありません: type=${(data) ? data.type : '<unknown>'}`)
  }

  getColumnItemId(index: number): string {
    return `${this.columnId}__column${index}`
  }

  getGridWidth(): number {
    const left = this.leftColumn.getBoundingClientRect().left
    const right = this.rightColumn.getBoundingClientRect().right
    return (right - left) / this.totalColumnSize
  }

  /**
   * マウス移動により、列方向のサイズ（itemSizes）がいくつ変化するのかを計算する
   * @param {Interact.InteractEvent} event - マウスイベント
   * @return {number} - 左列の itemSizes に対する変化量
   */
  getColumnMoveX(event: Interact.InteractEvent): number {
    const mouseMoveX = event.pageX - event.x0
    const gridWidth = this.getGridWidth()
    const columnMoveX = Math.floor((mouseMoveX + (gridWidth / 2)) / gridWidth)

    // 左列、右列の幅が0にならない値に丸めて返す
    return Math.min(Math.max(columnMoveX, 1 - this.leftColumnSize), this.rightColumnSize - 1)
  }
}
